/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  Calendar,
  Users,
  MapPin,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Shirt,
  Droplet,
  Search,
  Wallet,
  Ticket,
  Download,
  Filter,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useUser } from '../contexts/UserContext';
import axiosInstance from '../utils/axiosInstance';
import { API_URL, ORDER_STATUS_KEYS, getStatusBadge } from '../constant/constant';
import { useSearchParams } from 'react-router-dom';

// Mock data for fallback
const MOCK_PARTICIPANTS = [];

// Fetch event dashboard data from unified API
const fetchEventDashboard = async (vendorId, sessionToken, categoryId) => {
  try {
    const response = await axiosInstance.get(
      `${API_URL.BASE_ORDER}/rest/admin/dashboard/api/v1/order/per-vendor`,
      {
        params: { categoryId },
        headers: {
          accept: 'application/json',
          'X-Vendor-Id': vendorId,
          sessionToken: sessionToken,
          'X-USER-ROLE': 'Vendor',
        },
      }
    );

    const data = response.data || {};

    const participants = (data.participantDetailModelList || []).map((p) => {
      return {
        orderId: p.orderId || 'N/A',
        firstName: (p.name || '').trim(),
        lastName: '',
        emailId: p.emailId || '',
        mobileNo: p.mobileNo || '',
        age: p.age || 0,
        sex: p.gender || 'Not Specified',
        address: p.address || '',
        country: 'India',
        nationality: 'Indian',
        emergencyContact: p.emergencyMobileNo || '',
        tshirtSize: p.tshirtSize || 'N/A',
        bloodGroup: p.bloodGroup || 'N/A',
        runningGroupName: p.runningGroupName || '',
        productName: p.productName || '',
        ticketStatus: p.ticketStatus || 'Pending',
        ticketPrice: p.ticketPrice || 0,
        dob: p.dob,
      };
    });

    return {
      stats: {
        totalApprovedOrders: data.totalApprovedOrders || 0,
        totalPendingOrders: data.totalPendingOrders || 0,
        totalCancelledOrders: data.totalCancelledOrders || 0,
        totalApprovedAmt: data.totalApprovedAmt || 0,
        totalPendingAmt: data.totalPendingAmt || 0,
        totalCancelledAmt: data.totalCancelledAmt || 0,
        totalDiscount: data.totalDiscount || 0,
        totalApprovedTickets: data.totalApprovedTickets || 0,
        totalCancelledTickets: data.totalCancelledTickets || 0,
      },
      participants,
    };
  } catch {
    console.error('Error fetching event dashboard:');
    throw new Error('Error fetching event dashboard');
  }
};

// Process analytics data from participants
const processAnalytics = (participants) => {
  const genderCounts = {};
  const ageGroups = { '18-25': 0, '26-35': 0, '36-45': 0, '46-55': 0, '56+': 0 };
  const tshirtSizes = {};
  const bloodGroups = {};
  const cities = {};
  const states = {};
  const eventCategories = {};

  participants.forEach((p) => {
    // Gender
    const gender = p.sex || 'Not Specified';
    genderCounts[gender] = (genderCounts[gender] || 0) + 1;

    // Age - Calculate from DOB if age is not provided
    let age = p.age;
    if (!age && p.dob) {
      const dobParts = p.dob.split('-');
      if (dobParts.length === 3) {
        const dob = new Date(dobParts[2], dobParts[1] - 1, dobParts[0]);
        const today = new Date();
        age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
      }
    }

    if (age >= 18 && age <= 25) ageGroups['18-25']++;
    else if (age >= 26 && age <= 35) ageGroups['26-35']++;
    else if (age >= 36 && age <= 45) ageGroups['36-45']++;
    else if (age >= 46 && age <= 55) ageGroups['46-55']++;
    else if (age >= 56) ageGroups['56+']++;
    else if (age >= 26 && age <= 35) ageGroups['26-35']++;
    else if (age >= 36 && age <= 45) ageGroups['36-45']++;
    else if (age >= 46 && age <= 55) ageGroups['46-55']++;
    else if (age >= 56) ageGroups['56+']++;

    // T-Shirt
    const size = p.tshirtSize || 'Not Specified';
    if (size !== 'Not Specified') tshirtSizes[size] = (tshirtSizes[size] || 0) + 1;

    // Blood Group
    const bg = p.bloodGroup || 'Not Specified';
    if (bg !== 'Not Specified') bloodGroups[bg] = (bloodGroups[bg] || 0) + 1;

    // Cities & States
    const city = p.city || 'Not Specified';
    if (city && city !== 'Not Specified') cities[city] = (cities[city] || 0) + 1;

    const state = p.state || 'Not Specified';
    if (state && state !== 'Not Specified') states[state] = (states[state] || 0) + 1;

    // Categories
    if (p.productName) eventCategories[p.productName] = (eventCategories[p.productName] || 0) + 1;
  });

  return {
    genderCounts,
    ageGroups,
    tshirtSizes,
    bloodGroups,
    cities,
    states,
    eventCategories,
    totalParticipants: participants.length,
    withEmergencyContact: participants.filter((p) => p.emergencyContact).length,
  };
};

const flattenEventCategories = (categories = []) =>
  categories.flatMap((category) => {
    const children = flattenEventCategories(category.subCategories || []);
    return category.selectable || category.categoryType === 'EVENT' ? [category, ...children] : children;
  });

const fetchVendorEvents = async (vendorId, sessionToken) => {
  const response = await axiosInstance.get(
    `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/store/categories`,
    {
      params: { vendorId, type: 'EVENT' },
      headers: { sessionToken, 'X-USER-ROLE': 'Vendor' },
    }
  );
  return flattenEventCategories(response.data || []);
};

export default function EventDashboard() {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    searchParams.get('categoryId') || ''
  );
  const [participants, setParticipants] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalApprovedOrders: 0,
    totalPendingOrders: 0,
    totalCancelledOrders: 0,
    totalApprovedAmt: 0,
    totalPendingAmt: 0,
    totalCancelledAmt: 0,
    totalDiscount: 0,
    totalApprovedTickets: 0,
    totalCancelledTickets: 0,
  });

  const [activeTab, setActiveTab] = useState('overview');
  const [timeFilter, setTimeFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [currentPage, setCurrentPage] = useState(1);
  const participantsPerPage = 10;

  const isEventVendor =
    user?.attributeValues?.find((attr) => attr.name === 'isEventVendor')?.value === 'true';

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async () => {
      if (!isEventVendor || !user?.id) return;
      const sessionToken = localStorage.getItem('sessionToken') || user?.sessionToken;
      if (!sessionToken) return;

      try {
        const eventCategories = await fetchVendorEvents(user.id, sessionToken);
        if (!isMounted) return;
        setEvents(eventCategories);
        const requestedCategoryId = searchParams.get('categoryId');
        const initialCategoryId = eventCategories.some((event) => event.id === requestedCategoryId)
          ? requestedCategoryId
          : eventCategories[0]?.id || '';
        setSelectedCategoryId(initialCategoryId);
        if (initialCategoryId && initialCategoryId !== requestedCategoryId) {
          setSearchParams({ categoryId: initialCategoryId }, { replace: true });
        }
      } catch {
        if (isMounted) setError('Unable to load events. Please try again.');
      }
    };

    loadEvents();
    return () => {
      isMounted = false;
    };
  }, [user?.id, isEventVendor, user?.sessionToken, searchParams, setSearchParams]);

  useEffect(() => {
    let isMounted = true;

    const loadEventData = async () => {
      if (!isEventVendor || !user?.id) return;
      if (!selectedCategoryId) return;
      setLoading(true);
      setError(null);

      try {
        const vendorId = user.id;
        const sessionToken = localStorage.getItem('sessionToken') || user?.sessionToken;

        if (!vendorId || !sessionToken) {
          if (isMounted) {
            setError('Authentication required');
            setLoading(false);
          }
          return;
        }

        const dashboardData = await fetchEventDashboard(
          vendorId,
          sessionToken,
          selectedCategoryId
        );

        if (isMounted) {
          setDashboardStats(dashboardData.stats);
          setParticipants(dashboardData.participants);
          setLoading(false);
        }
      } catch {
        if (isMounted) {
          setParticipants(MOCK_PARTICIPANTS);
          setError('Using sample data. Unable to connect to server.');
          setLoading(false);
        }
      }
    };

    loadEventData();

    return () => {
      isMounted = false;
    };
  }, [user?.id, isEventVendor, selectedCategoryId, user?.sessionToken]);

  if (!isEventVendor) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-screen">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-screen">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const filteredParticipants = participants.filter((p) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      String(p.orderId || '')
        .toLowerCase()
        .includes(searchLower) ||
      p.firstName?.toLowerCase().includes(searchLower) ||
      p.lastName?.toLowerCase().includes(searchLower) ||
      p.emailId?.toLowerCase().includes(searchLower) ||
      p.mobileNo?.includes(searchTerm) ||
      p.address?.toLowerCase().includes(searchLower);

    const matchesStatus = statusFilter === 'All' || p.ticketStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const indexOfLastParticipant = currentPage * participantsPerPage;
  const indexOfFirstParticipant = indexOfLastParticipant - participantsPerPage;
  const currentParticipants = filteredParticipants.slice(
    indexOfFirstParticipant,
    indexOfLastParticipant
  );
  const totalPages = Math.ceil(filteredParticipants.length / participantsPerPage);

  const selectedEvent = events.find((event) => event.id === selectedCategoryId);

  const handleEventChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setSearchParams({ categoryId });
    setCurrentPage(1);
    setSearchTerm('');
    setStatusFilter('All');
  };

  const exportToExcel = async () => {
    const vendorId = user?.id;
    const sessionToken = localStorage.getItem('sessionToken') || user?.sessionToken;
    if (!vendorId || !sessionToken) {
      toast.error('Authentication required to export participants');
      return;
    }

    try {
      const response = await axiosInstance.get(
        `${API_URL.BASE_ORDER}/rest/admin/dashboard/api/v1/order/per-vendor/participants/export`,
        {
          responseType: 'blob',
          params: { categoryId: selectedCategoryId },
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'X-Vendor-Id': vendorId,
            sessionToken,
            'X-USER-ROLE': 'Vendor',
          },
        }
      );
      const downloadUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${(selectedEvent?.name || 'Event').replace(/[^a-z0-9]+/gi, '_')}_Participants_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting participants:', error);
      toast.error('Unable to export participant data');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 md:pb-0 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pt-4">
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Notice</h3>
              <p className="text-sm text-yellow-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="mb-2">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Event Dashboard</h1>
              {selectedEvent && (
                <p className="mt-1 text-sm text-gray-500">
                  {[selectedEvent.eventDate, selectedEvent.eventVenue].filter(Boolean).join(' | ')}
                </p>
              )}
            </div>
            <div className="w-full sm:w-80">
              <label className="block text-xs font-medium text-gray-500 mb-1">Event</label>
              <select
                value={selectedCategoryId}
                onChange={(event) => handleEventChange(event.target.value)}
                disabled={events.length === 0}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
              >
                {events.length === 0 && <option value="">No events available</option>}
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-4 mb-3 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'overview'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'analytics'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Analytics
          </button>
        </div>

        {activeTab === 'overview' && (
          <OverviewTab
            participants={currentParticipants}
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            totalParticipants={filteredParticipants.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            dashboardStats={dashboardStats}
            categoryId={selectedCategoryId}
            eventName={selectedEvent?.name}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab participants={participants} dashboardStats={dashboardStats} />
        )}
      </div>
    </div>
  );
}

// --- OVERVIEW TAB --- //
const OverviewTab = ({
  participants,
  currentPage,
  totalPages,
  setCurrentPage,
  totalParticipants,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  dashboardStats,
  categoryId,
  eventName,
}) => {
  const { user } = useUser();
  const [expandedParticipant, setExpandedParticipant] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Get all available status options
  const statusOptions = ['All', ...Object.values(ORDER_STATUS_KEYS)];

  const exportToExcel = async () => {
    const vendorId = user?.id;
    const sessionToken = localStorage.getItem('sessionToken') || user?.sessionToken;
    if (!vendorId || !sessionToken) {
      toast.error('Authentication required to export participants');
      return;
    }

    setExporting(true);
    try {
      const response = await axiosInstance.get(
        `${API_URL.BASE_ORDER}/rest/admin/dashboard/api/v1/order/per-vendor/participants/export`,
        {
          responseType: 'blob',
          params: { categoryId },
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'X-Vendor-Id': vendorId,
            sessionToken,
            'X-USER-ROLE': 'Vendor',
          },
        }
      );
      const downloadUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${(eventName || 'Event').replace(/[^a-z0-9]+/gi, '_')}_Participants_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting participants:', error);
      toast.error('Unable to export participant data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          {
            label: 'Approved Orders',
            value: dashboardStats?.totalApprovedOrders || 0,
            subtitle: `₹${(dashboardStats?.totalApprovedAmt || 0).toLocaleString()} revenue`,
            color: 'text-green-600',
          },
          {
            label: 'Pending Orders',
            value: dashboardStats?.totalPendingOrders || 0,
            subtitle: `₹${(dashboardStats?.totalPendingAmt || 0).toLocaleString()} pending`,
            color: 'text-yellow-600',
          },
          {
            label: 'Cancelled Orders',
            value: dashboardStats?.totalCancelledOrders || 0,
            subtitle: `₹${(dashboardStats?.totalCancelledAmt || 0).toLocaleString()} lost`,
            color: 'text-red-600',
          },
          {
            label: 'Approved Tickets',
            value: dashboardStats?.totalApprovedTickets || 0,
            subtitle: `${dashboardStats?.totalCancelledTickets || 0} cancelled`,
            color: 'text-purple-600',
          },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg py-2 px-4 border border-gray-100 shadow-sm"
          >
            <p className={`text-sm ${stat.color} font-medium mb-1`}>{stat.label}</p>
            <div className="flex items-center justify-between">
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-2">{stat.subtitle}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
        <div className="py-2 px-3 border-b border-gray-100">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-base font-semibold text-gray-900">
              All Participants ({totalParticipants})
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
              <div className="relative">
                <Filter className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm appearance-none bg-white cursor-pointer"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={exportToExcel}
                disabled={exporting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export Excel'}</span>
                <span className="sm:hidden">{exporting ? 'Exporting...' : 'Export'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="text-left min-w-[80px] py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[160px]">
                  Name
                </th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider max-w-[280px] min-w-[95px]">
                  Address
                </th>
                <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Age
                </th>
                <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  DOB
                </th>
                <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Gender
                </th>
                <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {participants.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-12 text-gray-500">
                    No participants found
                  </td>
                </tr>
              ) : (
                participants.map((participant, index) => (
                  <React.Fragment key={index}>
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-2 px-2 text-sm text-center font-mono text-purple-600">
                        {participant.orderId || 'N/A'}
                      </td>
                      <td className="py-2 px-2 text-sm font-medium text-gray-900">
                        <div>
                          {participant.firstName} {participant.lastName}
                        </div>
                        {participant.productName && (
                          <div className="text-xs text-purple-600 font-medium mt-1">
                            {participant.productName}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 text-sm text-gray-500">
                        <div>{participant.mobileNo}</div>
                        <div className="text-xs text-gray-400">{participant.emailId}</div>
                      </td>
                      <td className="py-2 px-2 max-w-[280px] text-sm text-gray-500">
                        <div className="whitespace-normal tracking-wider break-words">
                          {participant.address || 'N/A'}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-sm text-center text-gray-500">
                        {(() => {
                          let displayAge = participant.age;
                          // Calculate age from DOB if age is not provided
                          if (!displayAge && participant.dob) {
                            const dobParts = participant.dob.split('-');
                            if (dobParts.length === 3) {
                              const dob = new Date(dobParts[2], dobParts[1] - 1, dobParts[0]);
                              const today = new Date();
                              let age = today.getFullYear() - dob.getFullYear();
                              const monthDiff = today.getMonth() - dob.getMonth();
                              if (
                                monthDiff < 0 ||
                                (monthDiff === 0 && today.getDate() < dob.getDate())
                              ) {
                                age--;
                              }
                              displayAge = age;
                            }
                          }
                          return displayAge || 'N/A';
                        })()}
                      </td>
                      <td className="py-2 px-2 text-sm text-center text-gray-500">
                        {participant.dob || 'N/A'}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${participant.sex === 'Male' ? 'bg-blue-50 text-blue-700' : 'bg-pink-50 text-pink-700'}`}
                        >
                          {participant.sex}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-center">
                        {(() => {
                          const statusBadge = getStatusBadge(participant.ticketStatus);
                          return (
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}
                            >
                              {statusBadge.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-2 text-center text-sm font-semibold text-gray-900">
                        ₹{participant.ticketPrice?.toLocaleString() || 0}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <button
                          onClick={() =>
                            setExpandedParticipant(expandedParticipant === index ? null : index)
                          }
                          className="text-gray-400 hover:text-purple-600 transition-colors"
                        >
                          {expandedParticipant === index ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedParticipant === index && (
                      <tr>
                        <td colSpan="10" className="bg-gray-50/50 px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {participant.dob && (
                              <div>
                                <span className="text-gray-500 block text-xs mb-1">
                                  Date of Birth
                                </span>
                                <span className="font-medium text-gray-900">{participant.dob}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">T-Shirt Size</span>
                              <span className="font-medium text-gray-900">
                                {participant.tshirtSize || 'N/A'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">Blood Group</span>
                              <span className="font-medium text-gray-900">
                                {participant.bloodGroup || 'N/A'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">
                                Emergency Contact
                              </span>
                              <span className="font-medium text-gray-900">
                                {participant.emergencyContact || 'N/A'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">
                                Running Group
                              </span>
                              <span className="font-medium text-gray-900">
                                {participant.runningGroupName || 'N/A'}
                              </span>
                            </div>
                            {participant.dob && (
                              <div>
                                <span className="text-gray-500 block text-xs mb-1">
                                  Date of Birth
                                </span>
                                <span className="font-medium text-gray-900">{participant.dob}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Showing {(currentPage - 1) * 10 + 1} to{' '}
              {Math.min(currentPage * 10, totalParticipants)} of {totalParticipants}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- BENTO GRID ANALYTICS TAB --- //
const AnalyticsTab = ({ participants, dashboardStats }) => {
  const analytics = processAnalytics(participants);

  // Modernized palettes
  const COLORS = [
    '#8b5cf6',
    '#ec4899',
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#06b6d4',
    '#6366f1',
    '#14b8a6',
  ];
  const GENDER_COLORS = { Male: '#3b82f6', Female: '#ec4899', 'Not Specified': '#9ca3af' };

  // T-Shirt size colors
  const TSHIRT_COLORS = {
    XS: '#ec4899',
    S: '#8b5cf6',
    M: '#3b82f6',
    L: '#10b981',
    XL: '#f59e0b',
    XXL: '#ef4444',
    '2XL': '#ef4444',
    '3XL': '#06b6d4',
    '4XL': '#6366f1',
  };

  // Mapping Data
  const eventCategoryData = Object.entries(analytics.eventCategories).map(([name, value]) => ({
    name,
    value,
  }));
  const ageData = Object.entries(analytics.ageGroups).map(([name, value]) => ({
    name: name + ' yrs',
    value,
  }));
  const genderData = Object.entries(analytics.genderCounts).map(([name, value]) => ({
    name,
    value,
  }));
  const tshirtData = Object.entries(analytics.tshirtSizes).map(([name, value]) => ({
    name,
    value,
  }));

  // Custom function to render percentage labels cleanly inside the Pie chart slices
  const renderInnerLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null; // Hide labels for tiny slices
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
        style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.4)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Metrics specifically mapped
  const financialData = [
    { name: 'Approved', value: dashboardStats?.totalApprovedAmt || 0, fill: '#10b981' },
    { name: 'Cancelled', value: dashboardStats?.totalCancelledAmt || 0, fill: '#ef4444' },
    { name: 'Discount', value: dashboardStats?.totalDiscount || 0, fill: '#3b82f6' },
    { name: 'Pending', value: dashboardStats?.totalPendingAmt || 0, fill: '#f59e0b' },
  ];

  const orderData = [
    {
      name: 'Approved',
      orders: dashboardStats?.totalApprovedOrders || 0,
      tickets: dashboardStats?.totalApprovedTickets || 0,
    },
    {
      name: 'Cancelled',
      orders: dashboardStats?.totalCancelledOrders || 0,
      tickets: dashboardStats?.totalCancelledTickets || 0,
    },
    { name: 'Pending', orders: dashboardStats?.totalPendingOrders || 0, tickets: 0 },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-2xl shadow-sm border border-gray-100 text-sm">
          <p className="font-semibold text-gray-900 mb-1">{payload[0].payload.name}</p>
          {payload.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-2 mt-1">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: entry.fill || entry.color }}
              />
              <span className="text-gray-600 capitalize">
                {entry.name === 'value' ? 'Amount' : entry.name}:
              </span>
              <span className="font-medium text-gray-900">
                {entry.name === 'value' && entry.payload.fill
                  ? `₹${entry.value.toLocaleString()}`
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* ROW 1: Event Categories (Horizontal Bar) & Age (Vertical Bar) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-lg border border-gray-100 shadow-sm py-3 px-3 flex flex-col"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <h3 className="text-base font-semibold text-gray-900">Event Categories</h3>
            </div>
            <div className="text-right flex items-center gap-3">
              <p className="text-xs text-gray-500">Total Tickets</p>
              <p className="text-lg font-bold text-purple-600">
                {dashboardStats?.totalApprovedTickets || 0}
              </p>
            </div>
          </div>
          <div className="flex-1 min-h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={eventCategoryData}
                layout="vertical"
                margin={{ top: 0, right: 20, left: 5, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={true}
                  vertical={true}
                  stroke="#f3f4f6"
                />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
                <Bar
                  dataKey="value"
                  fill="#8b5cf6"
                  radius={[0, 3, 3, 0]}
                  maxBarSize={28}
                  label={{ position: 'right', fill: '#6b7280', fontSize: 12 }}
                >
                  {eventCategoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-gray-100 shadow-sm py-3 px-3 flex flex-col"
        >
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            <h3 className="text-base font-semibold text-gray-900">Age Distribution</h3>
          </div>
          <div className="flex-1 min-h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  dy={10}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
                <Bar dataKey="value" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* ROW 2: Demographics - Gender & T-Shirt Pie Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-3">
        {/* Gender */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg border border-gray-100 shadow-sm py-3 px-3 flex flex-col"
        >
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-pink-500" />
            <h3 className="text-base font-semibold text-gray-900">Gender Distribution</h3>
          </div>
          <div className="w-full h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  outerRadius="85%"
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                  label={renderInnerLabel}
                  labelLine={false}
                >
                  {genderData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={GENDER_COLORS[entry.name] || '#9ca3af'} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* T-Shirt with Pie Chart + Stats Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg border border-gray-100 shadow-sm py-3 px-3 flex flex-col"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Shirt className="w-5 h-5 text-indigo-500" />
              <h3 className="text-base font-semibold text-gray-900">T-Shirt Inventory</h3>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-lg font-bold text-indigo-600">
                {tshirtData.reduce((sum, item) => sum + item.value, 0)}
              </p>
            </div>
          </div>

          {/* Combined Layout: Pie Chart + Stats Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            {/* Pie Chart - Takes 1 column */}
            <div className="lg:col-span-1 h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tshirtData}
                    cx="50%"
                    cy="50%"
                    outerRadius="85%"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                    label={renderInnerLabel}
                    labelLine={false}
                  >
                    {tshirtData.map((entry, index) => {
                      const color =
                        TSHIRT_COLORS[entry.name] || COLORS[(index + 2) % COLORS.length];
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Stats Grid - Takes 2 columns */}
            <div className="lg:col-span-2 grid grid-cols-3 gap-2">
              {tshirtData.map((item, index) => {
                const color = TSHIRT_COLORS[item.name] || COLORS[index % COLORS.length];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.03 }}
                    className="bg-gray-50/50 rounded-lg p-2 border border-gray-100 flex flex-col items-center justify-center text-center hover:shadow-sm transition-all hover:scale-105"
                  >
                    <div className="w-3 h-3 rounded-full mb-1" style={{ backgroundColor: color }} />
                    <p className="text-xs font-semibold text-gray-600">{item.name}</p>
                    <p className="text-lg font-bold text-gray-900">{item.value}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {tshirtData.length === 0 && (
            <div className="flex items-center justify-center h-[240px] text-gray-400">
              <p className="text-sm">No T-shirt data available</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* ROW 3: Financial & Orders Metrics */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-3"
      >
        {/* Financial Amounts Bar Chart */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm py-3 px-3 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-600" />
              <h3 className="text-base font-semibold text-gray-900">Financial Revenue (INR)</h3>
            </div>
          </div>
          <div className="flex-1 min-h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={financialData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickFormatter={(value) => `₹${value}`}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={48}>
                  {financialData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Orders & Tickets Counts Bar Chart */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm py-3 px-3 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-orange-500" />
              <h3 className="text-base font-semibold text-gray-900">Orders & Tickets Count</h3>
            </div>
          </div>
          <div className="flex-1 min-h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={orderData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  dy={10}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                <Bar
                  dataKey="orders"
                  name="Total Orders"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={32}
                />
                <Bar
                  dataKey="tickets"
                  name="Total Tickets"
                  fill="#06b6d4"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={32}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
